/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { MsalProvider} from '@azure/msal-react';
import { useEffect } from 'react';
import './styles/App.css';
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import MainContent from './pages/ccmt';
import { checkIsDukePNGPortalSubdomain, getSubdomainFromUrl } from './utils/urlUtils';
import ChatBot from "./components/generic-chatbot";
import AgencyLookup from "./pages/agency-lookup/agency-lookup";
import PngAgencyLookup from "./pages/png-agency/agency-lookup";
import { addLog } from "./pages/agency-lookup/service";

const sanitizeLogValue = (value) =>
    String(value ?? "")
        .replace(/[^a-zA-Z0-9 ]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const App = ({ instance }) => {

    const companyCode = checkIsDukePNGPortalSubdomain(getSubdomainFromUrl(window.location.href));

    useEffect(() => {
        const queryParams = Object.fromEntries(new URLSearchParams(window.location.search).entries());
        const sanitizedQueryParams = Object.entries(queryParams).reduce((acc, [key, value]) => {
            acc[sanitizeLogValue(key)] = sanitizeLogValue(value);
            return acc;
        }, {});
        const sanitizedCompanyCode = sanitizeLogValue(companyCode || "Duke");
        const sanitizedPath = sanitizeLogValue(window.location.pathname);

        addLog(`${sanitizedCompanyCode} CCO Portal ${sanitizedPath}`, {
            url: sanitizeLogValue(window.location.href),
            queryParams: sanitizedQueryParams,
            companyCode: sanitizedCompanyCode
        });
    }, [companyCode]);

    return (
        <MsalProvider instance={instance}>
            <BrowserRouter>
                <div className="App">
                    <Routes>
                        <Route path="/" element={<Content companyCode={companyCode} pageName="main" />} />
                        <Route 
                            path="ccoassistant" 
                            element={<Content companyCode={companyCode} pageName="ccoassistant" />} />
                        
                        {/* OLD EWH Prod: DWJhNoImAbkeL8u65wZ9ZDSQ */}
                        <Route 
                            path="ewh" 
                            element={<Content companyCode={companyCode} pageName="ewh" />} />
                        {/* OLD EWH PROD: GJ4f1GFTY7S6tCGYSXPddiI3 */}
                        <Route 
                            path="heu" 
                            element={<Content companyCode={companyCode} pageName="heu" />} />
                        <Route 
                            path="sc_nm" 
                            element={<Content companyCode={companyCode} pageName="sc_nm" />} />
                        <Route path="agency-lookup/:token" element={<Content companyCode={companyCode} pageName="agencyLookup" />} />
                    </Routes>
                </div>
            </BrowserRouter>
            
        </MsalProvider>
    );
};

const Content = ({ companyCode, pageName }) => {
    switch (pageName) {
        case "main":
            return (companyCode === "PNG" ? <PageNotFound /> : <MainContent />);
        case "ccoassistant":
            return (<ChatBot 
                chatbotAppId={process.env.REACT_APP_CHATBOT_CCOASSIST} 
                heading="Contact Center Assistance" 
                title="Contact Center Assistance"
                newBot={true} />);
        case "ewh":
            return (<ChatBot 
                chatbotAppId={process.env.REACT_APP_CHATBOT_EWH} 
                heading="EnergyWise® Home" 
                title="EnergyWise® Home"
                newBot={true}/>);
        case "heu":
            return (<ChatBot 
                chatbotAppId={process.env.REACT_APP_CHATBOT_HEU} 
                heading="High Energy Usage Assistance" 
                title="High Energy Usage Assistance" 
                newBot={true}/>);
        case "sc_nm":
            return (<ChatBot 
                chatbotAppId={process.env.REACT_APP_CHATBOT_SC_NM} 
                heading="SC Net Metering" 
                title="SC Net Metering" 
                newBot={true}/>);
        case "agencyLookup":
            return (companyCode === "PNG" ? <PngAgencyLookup /> : <AgencyLookup />);
        default:
            return <div>Invalid page name</div>;
    }
};

const PageNotFound = () => {
    return <div>Page not found</div>;
};

export default App;
